import { useCallback, useState } from "react";
import { Shield, ShieldWarning, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { updateEnforcement } from "../lib/api";
import { Input } from "./ui/input";

const MODES = [
  {
    id: "off",
    label: "Off",
    icon: Shield,
    color: "#71717a",
    desc: "Clicks always redirect to the affiliate URL. Compliance is advisory only.",
  },
  {
    id: "warn",
    label: "Warn",
    icon: ShieldWarning,
    color: "#f59e0b",
    desc: "Failing variants are visually flagged but clicks still route through.",
  },
  {
    id: "block",
    label: "Block",
    icon: ShieldCheck,
    color: "#ccff00",
    desc: "Failing variants divert to the fallback URL (or 410) instead of your affiliate link.",
  },
];

export default function EnforcementPanel({ adSet, onAdSetUpdate }) {
  const [mode, setMode] = useState(adSet?.enforcement_mode || "off");
  const [fallback, setFallback] = useState(adSet?.fallback_url || "");
  const [saving, setSaving] = useState(false);

  const save = useCallback(
    async (newMode, newFallback) => {
      if (!adSet?.id) return;
      setSaving(true);
      try {
        const updated = await updateEnforcement(adSet.id, {
          enforcement_mode: newMode,
          fallback_url: newFallback,
        });
        onAdSetUpdate?.(updated);
        toast.success(`Enforcement set to ${newMode.toUpperCase()}`);
      } catch (err) {
        toast.error(err.response?.data?.detail || "Failed to update");
      } finally {
        setSaving(false);
      }
    },
    [adSet?.id, onAdSetUpdate],
  );

  const onModeChange = (id) => {
    setMode(id);
    save(id, fallback);
  };

  const onFallbackBlur = () => {
    if (fallback !== (adSet?.fallback_url || "")) save(mode, fallback);
  };

  const activeMeta = MODES.find((m) => m.id === mode) || MODES[0];
  const ActiveIcon = activeMeta.icon;

  return (
    <div
      className="bg-[#0a0a0a] border border-white/10 p-4"
      data-testid="enforcement-panel"
    >
      <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
        <div className="flex items-center gap-3">
          <ActiveIcon
            size={18}
            weight="duotone"
            style={{ color: activeMeta.color }}
          />
          <div>
            <div className="font-display font-bold text-sm">
              Enforcement · {activeMeta.label}
            </div>
            <div className="font-mono text-[10px] text-zinc-500">
              {activeMeta.desc}
            </div>
          </div>
        </div>
        <div className="flex gap-1">
          {MODES.map((m) => {
            const isActive = mode === m.id;
            const Icon = m.icon;
            return (
              <button
                key={m.id}
                onClick={() => onModeChange(m.id)}
                disabled={saving}
                data-testid={`enforcement-${m.id}`}
                className={`px-3 py-2 border font-mono text-[10px] uppercase tracking-wider flex items-center gap-1.5 transition-colors ${
                  isActive
                    ? "border-[#ccff00] bg-[#ccff00]/10 text-[#ccff00]"
                    : "border-white/10 text-zinc-400 hover:border-white/30 hover:text-white"
                }`}
              >
                <Icon size={11} weight={isActive ? "fill" : "regular"} />
                {m.label}
              </button>
            );
          })}
        </div>
      </div>
      {mode === "block" && (
        <div className="pt-3 border-t border-white/5">
          <div className="label-mono mb-2">fallback url (optional)</div>
          <Input
            data-testid="fallback-url-input"
            value={fallback}
            onChange={(e) => setFallback(e.target.value)}
            onBlur={onFallbackBlur}
            placeholder="https://your-site.com/compliance-block"
            className="rounded-none bg-[#121212] border-white/10 h-10 font-mono text-xs"
          />
          <div className="font-mono text-[10px] text-zinc-500 mt-1.5">
            Empty → blocked clicks return HTTP 410. Any URL entered will get
            utm_medium=blocked appended for reporting.
          </div>
        </div>
      )}
    </div>
  );
}
